import type { LibraryScopeFilter, LibrarySortOption } from './types';

// `labelKey` IS A LITERAL UNION RATHER THAN A `TranslationKey`, and the
// difference is the zone rather than a loosening. This door is inert — relative
// imports and `@heidi/schema` only — so `@heidi/runtime/i18n` cannot be reached
// from here to name the type. `as const` keeps each key as its own literal, so
// a call site still passes `t(option.labelKey)` and `TranslationKey` checks the
// value at the call rather than here; a typo fails at the first consumer
// instead of silently widening to `string`.
export const SORT_OPTIONS = [
  { value: 'date', labelKey: 'library.sortOptions.date' },
  { value: 'name', labelKey: 'library.sortOptions.name' },
  { value: 'type', labelKey: 'library.sortOptions.type' },
] as const satisfies readonly { value: LibrarySortOption; labelKey: string }[];

export const SCOPE_FILTER_OPTIONS = [
  { value: 'all' as const },
  { value: 'team' as const },
  { value: 'personal' as const },
  { value: 'sharepoint' as const },
] satisfies { value: LibraryScopeFilter }[];

// Per-collection domain caps. Mirrors the ml-scribe Pydantic validators —
// keep these in lockstep with the BE constants:
//   `database/mongo/models/source_list.py::MAX_DOMAINS_PER_SOURCE_LIST`
//   `evidence/services/user_document_storage.py::MAX_DOMAINS_PER_COLLECTION`
// Over-cap POSTs surface as a 400 from the API (see `apis/v2/source_list/__init__.py`
// and `evidence/collections_router.py`). The FE caps preemptively so the user
// only ever sees the slice that will actually be saved — silent truncation
// at write time was the previous source of "I added 305 but only 100 stuck".
export const MAX_DOMAINS_PER_SOURCE_LIST = 100;
export const MAX_DOMAINS_PER_COLLECTION = 300;

/**
 * Per-collection domain cap that the active My Library entity respects.
 * Switches with the `release-unified-collections` flag because the two
 * entities have different BE-enforced ceilings (Tavily allows 300 in the
 * unified path; the legacy SourceList capped at 100 historically).
 *
 * THE FLAG IS AN ARGUMENT NOW, not a getter call. An inert module cannot read
 * a flag at all, and the three call sites already read
 * `IS_UNIFIED_COLLECTIONS_ENABLED()` for their own branching, so they hand in
 * the value they already have rather than the module resolving it a second
 * time at a different moment in the same render.
 */
export const getMaxDomainsPerCollection = (isUnifiedEnabled: boolean): number =>
  isUnifiedEnabled ? MAX_DOMAINS_PER_COLLECTION : MAX_DOMAINS_PER_SOURCE_LIST;
